/**
 * Unit conversion helpers.
 *
 * The physics core is strictly SI (see `constants.ts`). These functions exist so
 * that the *only* place kilometres, degrees and tonnes appear is at the boundary
 * between the engine and the user interface.
 */

export const DEG = Math.PI / 180;
export const RAD = 180 / Math.PI;

export const degToRad = (deg: number): number => deg * DEG;
export const radToDeg = (rad: number): number => rad * RAD;

export const kmToM = (km: number): number => km * 1000;
export const mToKm = (m: number): number => m / 1000;

export const tonneToKg = (t: number): number => t * 1000;
export const kgToTonne = (kg: number): number => kg / 1000;

export const kNToN = (kn: number): number => kn * 1000;
export const nToKN = (n: number): number => n / 1000;

export const paToKPa = (pa: number): number => pa / 1000;
export const paToBar = (pa: number): number => pa / 1e5;

/** Metres per second to kilometres per hour. */
export const msToKmh = (ms: number): number => ms * 3.6;

/** Acceleration in m/s^2 expressed in multiples of standard gravity. */
export const msToGee = (ms2: number): number => ms2 / 9.80665;

/**
 * Normalise an angle to [0, 2*pi).
 *
 * Uses a modulo rather than a while-loop so that a wildly out-of-range input
 * (which happens when a mean anomaly accumulates over thousands of orbits) costs
 * the same as an in-range one.
 */
export function wrapTwoPi(angle: number): number {
  const t = angle % (2 * Math.PI);
  return t < 0 ? t + 2 * Math.PI : t;
}

/** Normalise an angle to (-pi, pi]. */
export function wrapPi(angle: number): number {
  const t = wrapTwoPi(angle);
  return t > Math.PI ? t - 2 * Math.PI : t;
}

/** Normalise degrees to [0, 360). */
export function wrap360(deg: number): number {
  const t = deg % 360;
  return t < 0 ? t + 360 : t;
}

/** Normalise degrees to (-180, 180] — the range longitudes are displayed in. */
export function wrap180(deg: number): number {
  const t = wrap360(deg);
  return t > 180 ? t - 360 : t;
}

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Clamp to [-1, 1] before an acos/asin so floating-point overshoot cannot yield NaN. */
export const clampUnit = (v: number): number => clamp(v, -1, 1);

/** Linear interpolation. */
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Inverse linear interpolation, clamped to [0, 1]. */
export const inverseLerp = (a: number, b: number, v: number): number =>
  a === b ? 0 : clamp((v - a) / (b - a), 0, 1);

/**
 * Format a duration in seconds as `[Dd ]HH:MM:SS` — the mission-elapsed-time
 * convention used on the timeline and status bar.
 */
export function formatDuration(seconds: number): string {
  const sign = seconds < 0 ? '-' : '';
  const s = Math.abs(Math.floor(seconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const hms = `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  return days > 0 ? `${sign}${days}d ${hms}` : `${sign}${hms}`;
}

/** Engineering-friendly value formatting: picks a unit prefix and fixed precision. */
export function formatMetres(m: number, digits = 2): string {
  if (!Number.isFinite(m)) return '—';
  const abs = Math.abs(m);
  if (abs >= 1e9) return `${(m / 1e9).toFixed(digits)} Gm`;
  if (abs >= 1e6) return `${(m / 1e6).toFixed(digits)} Mm`;
  if (abs >= 1000) return `${(m / 1000).toFixed(digits)} km`;
  return `${m.toFixed(digits)} m`;
}
